// @givmo/app-charity — ACH-entry validation (Zod) (CPA-WEB4).
//
// Client-side shape validation for the ONE moment a full bank account number exists in the app: the ACH
// entry form. The numbers are validated here, submitted once, and then CLEARED — they are never read
// back (no read-back API) and never rendered post-entry (§3.4). The backend re-validates + encrypts into
// the vault; this schema is purely to gate the submit button and give inline feedback.
//
// Rules (US ACH), the backend's own (`_validate_ach_input`, CPA-BE46):
//   - routing_number: exactly 9 digits (ABA routing transit number).
//   - account_number: 5–17 digits (the ACH DFI account-number field is up to 17 chars; no bank
//     account number has only 4, and a 4-digit one would make the last-4 mask the whole number,
//     D-CP-82).
//   - both: spaces and dashes between the digits are dropped as the backend drops them; any other
//     character is refused, never dropped (`dropNumberSeparators`).
//   - account_holder_name: non-empty; matched against the charity's legal name by the backend (§4).
//   - bank_name: optional label.

import { z } from 'zod';

/** Exactly 9 digits (ABA routing transit number). */
export const ROUTING_NUMBER_REGEX = /^\d{9}$/;

/** 5–17 digits (the ACH account-number field length, from the backend's 5-digit floor). */
export const ACCOUNT_NUMBER_REGEX = /^\d{5,17}$/;

export const ROUTING_NUMBER_MESSAGE = 'Routing number must be 9 digits';
export const ROUTING_NUMBER_CHECKSUM_MESSAGE =
  'Routing number failed validation — double-check with your bank';
export const ACCOUNT_NUMBER_MESSAGE = 'Enter a valid account number (5–17 digits)';
export const ACCOUNT_NUMBER_CONFIRM_MESSAGE = 'Account numbers must match';
export const ACCOUNT_HOLDER_MESSAGE = 'Enter the account-holder name';

/** ACH entry: routing (9 digits) + account number (5–17 digits) + holder name + optional bank name. */
export function hasValidAbaChecksum(value: string): boolean {
  if (!ROUTING_NUMBER_REGEX.test(value)) return false;
  const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1] as const;
  let checksum = 0;
  for (let index = 0; index < value.length; index += 1) {
    checksum += (value.charCodeAt(index) - 48) * weights[index]!;
  }
  return checksum % 10 === 0;
}

export const achSchema = z
  .object({
    routing_number: z
      .string()
      .regex(ROUTING_NUMBER_REGEX, ROUTING_NUMBER_MESSAGE)
      .refine(hasValidAbaChecksum, ROUTING_NUMBER_CHECKSUM_MESSAGE),
    account_number: z.string().regex(ACCOUNT_NUMBER_REGEX, ACCOUNT_NUMBER_MESSAGE),
    confirm_account_number: z.string().regex(ACCOUNT_NUMBER_REGEX, ACCOUNT_NUMBER_MESSAGE),
    account_holder_name: z.string().trim().min(1, ACCOUNT_HOLDER_MESSAGE),
    bank_name: z.string().trim().optional(),
  })
  .refine((values) => values.account_number === values.confirm_account_number, {
    path: ['confirm_account_number'],
    message: ACCOUNT_NUMBER_CONFIRM_MESSAGE,
  });
export type AchFormValues = z.infer<typeof achSchema>;

/** The empty ACH form — also the value the form is RESET to after a submit (drops the entered number). */
export const EMPTY_ACH_FORM: AchFormValues = {
  routing_number: '',
  account_number: '',
  confirm_account_number: '',
  account_holder_name: '',
  bank_name: '',
};

/**
 * Drop the separators a person types between a bank number's digits (whitespace, any dash) and keep
 * every other character, so the schema refuses it: the backend's `ach_number_digits`, for the routing
 * and account numbers alike. Dropping a letter O typed for a zero leaves a shorter number that can
 * still fit 5–17 digits, a different account the name match cannot catch.
 */
export function dropNumberSeparators(value: string): string {
  return value.replace(/[\s\p{Pd}]/gu, '');
}
